import React from 'react';
import { Briefcase, Clock, Award } from 'lucide-react';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';

interface WorkHistoryStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
}

const WORKFORCE_OPTIONS = [
  'Continue with studies',
  'Move into the workforce',
  'Both',
];

export const WorkHistoryStep: React.FC<WorkHistoryStepProps> = ({
  formData,
  errors,
  updateFormData,
  setErrors,
}) => {
  const handleChange = (field: keyof ApplicationFormData, value: any) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  return (
    <div>
      <h5 className="fw-bold mb-3">E. Work History</h5>
      <p className="text-muted mb-3">
        <small>Note: if you do not have any work history, do not worry as this will not affect your eligibility</small>
      </p>
      
      <div className="row">
        <div className="col-md-6 mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="workedAsHealthCareAssistant"
              checked={formData.workedAsHealthCareAssistant}
              onChange={(e) => handleChange('workedAsHealthCareAssistant', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="workedAsHealthCareAssistant">
              Have you ever worked as a Health Care Assistant?
            </label>
          </div>
          {formData.workedAsHealthCareAssistant && (
            <div className="mt-2">
              <label className="form-label fw-semibold">How long did you work as one?</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Clock size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  value={formData.healthCareAssistantDuration || ''}
                  onChange={(e) => handleChange('healthCareAssistantDuration', e.target.value)}
                  placeholder="e.g., 2 years, 6 months"
                />
              </div>
            </div>
          )}
        </div>

        <div className="col-md-6 mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="otherHealthcareQualifications"
              checked={formData.otherHealthcareQualifications}
              onChange={(e) => handleChange('otherHealthcareQualifications', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="otherHealthcareQualifications">
              Do you have any other qualifications in Healthcare?
            </label>
          </div>
          {formData.otherHealthcareQualifications && (
            <div className="mt-2">
              <label className="form-label fw-semibold">What are they? (either in New Zealand or Overseas)</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Award size={18} className="text-muted" />
                </span>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.healthcareQualificationsDetails || ''}
                  onChange={(e) => handleChange('healthcareQualificationsDetails', e.target.value)}
                  placeholder="List your healthcare qualifications"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">
            Do you intend to continue with your studies or move into the workforce after completing the course? <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <Briefcase size={18} className="text-muted" />
            </span>
            <select
              className={`form-select ${errors.continueStudiesOrWorkforce ? 'is-invalid' : ''}`}
              value={formData.continueStudiesOrWorkforce || ''}
              onChange={(e) => handleChange('continueStudiesOrWorkforce', e.target.value)}
            >
              <option value="">Select option</option>
              {WORKFORCE_OPTIONS.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          {errors.continueStudiesOrWorkforce && (
            <div className="invalid-feedback d-block">{errors.continueStudiesOrWorkforce}</div>
          )}
        </div>
      </div>
    </div>
  );
};

